// ##BEGIN:DEVELOPERCODE##

import { useState, useCallback, useMemo, useEffect } from "react";
import { IDiscountRequest } from "../../../entities-generated/models/project/discount-request";
import { IProjectIncentiveEditingModel, ProjectIncentiveEditingModel } from "../../../entities-generated/models/project/project-incentive-editing-model";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { MutableFormValueService, ValidatedFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrWhitespace } from "../../../framework/utils";
import { any, none, where } from "../../../framework/utils/collection";
import { ProjectEvents } from "../events";
import { IIncentiveEditorFormModel, IIncentiveEditorValueModel, IIncentiveEditorModelChange } from "./incentive-editor-iface";
import { ImageApiClient } from "../../../services/image";
import { isEmptyString, isMeaningfulString } from "../../../framework/utils/string";
import { getUtcNowUnixTimestamp } from "../../../framework/utils/date-time";
import { IUpdateIncentivesRequest } from "../../../entities-generated/models/project/update-incentives-request";
import { getAnnotatedCustomerNotes } from "../services/customer-note";

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:taxExemptionFormUrl/mutable/string/string%%
export const taxExemptionFormUrl = new (class taxExemptionFormUrlService extends MutableFormValueService<IProjectIncentiveEditingModel, string, string> {
    public initialize(data: IProjectIncentiveEditingModel): void {
        this.value = data.taxExemptionFormUrl;
    }

    public applyToDataModel(data: IProjectIncentiveEditingModel): void {
        data.taxExemptionFormUrl = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

    public setInputValue(value: string): void {
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value;
    }

    public onUpdate(value: string): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'taxExemptionFormUrl' // id
    ,'' // description
);
// %%END:taxExemptionFormUrl/mutable/string/string%%

// %%BEGIN:taxExemptionFormUrlUploader/validated/File[]/File[]%%
export const taxExemptionFormUrlUploader = new (class taxExemptionFormUrlUploaderService extends ValidatedFormValueService<IProjectIncentiveEditingModel, File[], File[]> {
    public initialize(data: IProjectIncentiveEditingModel): void {
        this.setInputValue(new BwjInput<File[]>([]));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectIncentiveEditingModel): void {}

    public getDefaultValue(): BwjInput<File[]> {
        return new BwjInput<File[]>([]);
    }

    public getInputValue(): BwjInput<File[]> {
        return this.value;
    }

    public setInputValue(value: BwjInput<File[]>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): File[] {
        return this.value.value;
    }

    public validate(value: File[]): Promise<BwjInput<File[]>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: File[]): void {
        const pendingResult = new BwjInput<File[]>(value);
        pendingResult.id = this.id;
        pendingResult.description = this.description;
        pendingResult.validity = BwjValidityState.Indeterminate;
        pendingResult.message = 'One moment please...';
        this.setInputValue(pendingResult);
        !!this.publishChange && this.publishChange(this.id);

        let exImg: string = taxExemptionFormUrl.getOutputValue();
        (isNotNullOrWhitespace(exImg) ? ImageApiClient.removeImage(exImg) : Promise.resolve())
        .then(() => true)
        .catch(() => {
            const verr = this.getDefaultValue();
            verr.validity = BwjValidityState.ValidationError;
            verr.message = 'Error occurred while preparing upload';
            this.setInputValue(verr);
            !!this.publishChange && this.publishChange(this.id);
            return false;
        })
        .then(ready => {
            if(ready) {
                taxExemptionFormUrl.setInputValue('');
                return ImageApiClient.uploadImage(value);
            }
            return undefined;
        })
        .then(resp => {
            if(!resp) { return; }
            taxExemptionFormUrl.setInputValue(resp.imageUrls[0]);

            const result = this.getDefaultValue();
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id);
        })
        .catch(() => {
            const verr = this.getDefaultValue();
            verr.validity = BwjValidityState.ValidationError;
            verr.message = 'Image upload failed';
            this.setInputValue(verr);
            !!this.publishChange && this.publishChange(this.id);
        })
    }

})(
    'taxExemptionFormUrlUploader' // id
    ,'State tax exemption form' // description
);
// %%END:taxExemptionFormUrlUploader/validated/File[]/File[]%%

// %%BEGIN:notes/validated/string/string%%
export const notes = new (class notesService extends ValidatedFormValueService<IProjectIncentiveEditingModel, string, string> {
    public initialize(data: IProjectIncentiveEditingModel): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectIncentiveEditingModel): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'notes' // id
    ,'Customer profile notes' // description
);
// %%END:notes/validated/string/string%%

// %%BEGIN:discounts/validated/string/IDiscountRequest[]%%
export const discounts = new (class discountsService extends ValidatedFormValueService<IProjectIncentiveEditingModel, string, IDiscountRequest[]> {
    public initialize(data: IProjectIncentiveEditingModel): void {
        const strDiscounts = data.discounts.map(d => {
            const exp = new Date(d.expiresUtc);
            const flat = d.flatAmount !== 0 ? `|${d.flatAmount}` : '';
            const pct = d.percentage !== 0 ? `%${d.percentage}` : '';
            const line = `${d.code}${pct}${flat}>${exp.getFullYear()}-${(exp.getMonth() + 1)}-${exp.getDate()}`;
            return line;
        });
        this.setInputValue(new BwjInput<string>(strDiscounts.join('\n')));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IProjectIncentiveEditingModel): void {
        data.discounts = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): IDiscountRequest[] {
        const arr = this.value.value.split('\n').map(x => this.toDiscountRequest(x)) as any[];
        const value = where(arr, x => !!x && !x.err) as IDiscountRequest[];
        return value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        if(isMeaningfulString(value)) {
            const all = value.split('\n').map(x => this.toDiscountRequest(x)) as any[];
            const errs = where(all, x => !!x && !!x.err) as { err: string }[];
            if(errs.length > 0) {
                result.validity = BwjValidityState.Invalid;
                result.message = errs[0].err;
            }
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }
    
    private toDiscountRequest(value: string) {
        if(isEmptyString(value)) { return null; }
        const req: IDiscountRequest = {
            code: '',
            flatAmount: 0,
            percentage: 0,
            expiresUtc: 0
        };
        let parts = value.split('>');
        if(parts.length !== 2) { return { err: 'Multiple date separators encountered' }; }

        const arrDate = parts[1].split('-').map(x => parseInt(x));
        if(arrDate.length !== 3) { return { err: 'Date format must be YYYY-M-D' }; }
        if(any(arrDate, x => isNaN(x))) { return { err: 'Part of date is invalid' }; }
        req.expiresUtc = (new Date(arrDate[0], arrDate[1] - 1, arrDate[2])).getTime();
        if(req.expiresUtc < getUtcNowUnixTimestamp()) { return { err: 'Expiration date already expired' }; }

        if(parts[0].indexOf('|') !== -1) {
            parts = parts[0].split('|');
            if(parts[1].indexOf('%') !== -1) { return { err: 'Flat amount must be specified after a percentage value' } }
            const flat = parseInt(parts[1]);
            if(isNaN(flat)) { return { err: 'Flat amount is an invalid number' }; }
            req.flatAmount = flat;
        }

        if(parts[0].indexOf('%') !== -1) {
            parts = parts[0].split('%');
            const pct = parseInt(parts[1]);
            if(isNaN(pct)) { return { err: 'Percentage amount is an invalid number' }; }
            req.percentage = pct;
        }

        if(req.flatAmount === 0 && req.percentage === 0) { return { err: 'Discount amount is missing' }; }
        if(isEmptyString(parts[0])) { return { err: 'Discount has no description' }; }
        req.code = parts[0].trim();

        return req;
    }

})(
    'discounts' // id
    ,'Discounts' // description
);
// %%END:discounts/validated/string/IDiscountRequest[]%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IIncentiveEditorFormModel => {
    return {
       taxExemptionFormUrl: taxExemptionFormUrl.getInputValue(),
       taxExemptionFormUrlUploader: taxExemptionFormUrlUploader.getInputValue(),
       notes: notes.getInputValue(),
       discounts: discounts.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IIncentiveEditorValueModel => {
    return {
       taxExemptionFormUrl: taxExemptionFormUrl.getOutputValue(),
       taxExemptionFormUrlUploader: taxExemptionFormUrlUploader.getOutputValue(),
       notes: notes.getOutputValue(),
       discounts: discounts.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        taxExemptionFormUrlUploader.getInputValue(),
        notes.getInputValue(),
        discounts.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IIncentiveEditorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(ProjectEvents.IncentiveEditorDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IIncentiveEditorFormModel>> => {
    const promises = [
        taxExemptionFormUrlUploader.formValidateAndUpdate(),
        notes.formValidateAndUpdate(),
        discounts.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IIncentiveEditorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IIncentiveEditorValueModel, dataModel: IProjectIncentiveEditingModel } = {
    dataModel: new ProjectIncentiveEditingModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IProjectIncentiveEditingModel) => {
    const dataModel = data || new ProjectIncentiveEditingModel();

    taxExemptionFormUrl.initialize(dataModel);
    taxExemptionFormUrlUploader.initialize(dataModel);
    notes.initialize(dataModel);
    discounts.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IIncentiveEditorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
taxExemptionFormUrl.setChangePublisher(broadcastChange);
taxExemptionFormUrlUploader.setChangePublisher(broadcastChange);
notes.setChangePublisher(broadcastChange);
discounts.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new ProjectIncentiveEditingModel();

    taxExemptionFormUrl.applyToDataModel(dataModel);
    taxExemptionFormUrlUploader.applyToDataModel(dataModel);
    notes.applyToDataModel(dataModel);
    discounts.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IIncentiveEditorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], ProjectEvents.IncentiveEditorDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IIncentiveEditorFormModel => {
    const [getter, setter] = useState<IIncentiveEditorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IIncentiveEditorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IIncentiveEditorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('IncentiveEditorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('IncentiveEditorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toUpdateRequest = (rejectProject: boolean) => {
    const v = toValueModel();
    const request: IUpdateIncentivesRequest = {
        discounts: v.discounts,
        notes: getAnnotatedCustomerNotes(v.notes, 'Incentivizing Purchase'),
        taxExemptionFormUrl: v.taxExemptionFormUrl
    };
    return request;
}

// ##END:DEVELOPERCODE##
